/**
 * Device Initialization Screen
 *
 * Manager/admin enters full credentials and selects a store location
 * to initialize this POS terminal. On success, a httpOnly device session
 * cookie is set by the backend, and the app transitions to device_ready.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import teletimeLogoWhite from '../assets/logos/teletime-logo-white-400.png';

function DeviceInitScreen() {
  const { initializeDevice, error, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Fetch available locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get('/locations');
        if (response?.success && Array.isArray(response.data)) {
          setLocations(response.data);
          if (response.data.length === 1) {
            setLocationId(String(response.data[0].id));
          }
        }
      } catch (err) {
        // Fallback: hardcoded locations for offline scenarios
        setLocations([
          { id: 1, name: 'Mississauga' },
          { id: 2, name: 'Brampton' },
          { id: 3, name: 'Etobicoke' },
          { id: 4, name: 'Springdale' }
        ]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Clear error on input change
  useEffect(() => {
    if (error) clearError();
  }, [email, password, locationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !locationId) return;

    setSubmitting(true);
    const result = await initializeDevice(email, password, Number(locationId));
    setSubmitting(false);

    if (!result.success) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #0d1117 0%, #131d2e 35%, #0f2847 65%, #0d1117 100%)' }}
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, transparent 70%)' }}
      />

      {/* Logo */}
      <div className="text-center mb-8 relative z-10" style={{ animation: 'fadeSlideIn 0.5s ease-out both' }}>
        <img
          src={teletimeLogoWhite}
          alt="Teletime"
          className="w-72 max-w-[85vw] h-auto mx-auto mb-3"
          style={{ filter: 'drop-shadow(0 4px 20px rgba(59, 130, 246, 0.2))' }}
        />
        <div className="flex items-center justify-center gap-2 mt-3">
          <div className="h-px w-8 bg-gray-600" />
          <span className="text-gray-500 text-xs font-medium tracking-widest uppercase">Terminal Setup</span>
          <div className="h-px w-8 bg-gray-600" />
        </div>
      </div>

      {/* Init Card */}
      <div
        className={`w-full max-w-sm relative z-10 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}
        style={{ animation: shake ? undefined : 'fadeSlideIn 0.5s ease-out 0.1s both' }}
      >
        <div
          className="rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: 'rgba(255, 255, 255, 0.97)', boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)' }}
        >
          {/* Accent bar */}
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #2563eb, #e94560)' }} />

          <div className="p-7 pt-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Initialize Terminal</h2>
              <p className="text-gray-500 text-sm mt-1">Manager credentials required</p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-center gap-2.5">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Location Select */}
              <div>
                <label htmlFor="device-location" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Store Location
                </label>
                <select
                  id="device-location"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  required
                  disabled={loadingLocations}
                  className="w-full h-12 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm text-gray-900 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/10 appearance-none"
                >
                  <option value="">Select location...</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="init-email" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Manager Email
                </label>
                <input
                  id="init-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="manager@teletime.ca"
                  required
                  autoComplete="email"
                  className="w-full h-12 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/10"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="init-password" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="init-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter manager password"
                    required
                    autoComplete="current-password"
                    className="w-full h-12 px-4 pr-12 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || !email || !password || !locationId}
                className="w-full h-12 mt-2 text-white font-semibold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-[15px]"
                style={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  boxShadow: (submitting || !email || !password || !locationId)
                    ? 'none'
                    : '0 4px 14px rgba(37, 99, 235, 0.4)'
                }}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Initializing...
                  </>
                ) : (
                  'Initialize Terminal'
                )}
              </button>
            </form>

            <div className="flex items-center justify-center gap-1.5 mt-5 text-gray-400 text-xs">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>Manager or admin access required</span>
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-gray-600/40 text-xs mt-8 relative z-10">
        &copy; {new Date().getFullYear()} Teletime Inc. &middot; www.teletime.ca
      </p>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}

export default DeviceInitScreen;
